/**
 * Módulo `scene` — sala de operaciones del 51 Escuadrón.
 *
 * Punto de entrada del módulo. Inicializa:
 *  - El reloj del HUD.
 *  - El portal de tooltips para hotspots con `.no-hover-elev`.
 *  - El editor visual de hotspots (tecla E).
 *
 * Cuando se añadan más responsabilidades a la escena (efectos ambientales,
 * minimapa, etc.), se añaden aquí.
 */

import { me } from '../auth/api.js';
import { startClock } from './clock.js';
import { initTooltipPortal } from './tooltip-portal.js';
import { initEditor } from './editor.js';
import { startTimeOfDayWatcher } from './time-of-day.js';
import { startTimeOfDayCycle } from './time-of-day-cycle.js';
import { initShutdownTransition } from './transitions.js';

/** Inicializa el módulo escena cuando el DOM está listo. */
export function initScene(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}

function boot(): void {
  startClock();
  initTooltipPortal();
  // Editor visual de hotspots (tecla E): herramienta de ajuste fino de la
  // escena. Disponible siempre en dev; en producción, sólo para el
  // superadmin. El gate vive en initEditorIfAllowed — necesita consultar
  // /api/auth/me, por eso es async y se lanza con `void` sin bloquear el boot.
  void initEditorIfAllowed();
  // Transición CRT-off al pulsar un hotspot. La sala es pública: las
  // páginas a las que llevan los hotspots se montan con `allowAnonymous`
  // y enseñan la vista a cualquiera; crear/editar sigue pidiendo sesión.
  initShutdownTransition();
  // Watcher de hora del día: el inline en <head> ya fijó el estado
  // inicial; esto solo refresca cada minuto durante la sesión.
  startTimeOfDayWatcher();
  // Ciclador opcional: activado por URL `?cycle` o por preferencia CRT
  // (CONFIG → MODO → CICLO Xs). El watcher de arriba detecta ambas
  // activaciones y se inhibe, así que no hay conflicto.
  startTimeOfDayCycle();
}

/**
 * Monta el editor visual de hotspots según el contexto de ejecución:
 *  - En dev (`npm run dev`, sólo en local): siempre, sin necesidad de
 *    sesión — es la herramienta que se usa al desarrollar la escena.
 *  - En producción: sólo si hay sesión activa y el rol es `superadmin`.
 *    El resto de pilotos nunca recibe el editor ni el hint de la tecla E.
 *
 * Async porque el chequeo de rol en producción depende de `GET
 * /api/auth/me`. Se invoca con `void` para no bloquear el resto del boot.
 */
async function initEditorIfAllowed(): Promise<void> {
  if (import.meta.env.DEV) {
    initEditor();
    return;
  }
  const result = await me();
  if (result.ok && result.data.user.role === 'superadmin') {
    // Sin hint flotante en producción: el superadmin ya conoce la tecla E.
    initEditor({ showHint: false });
  }
}
